import React from "react";
import { useTranslation } from "react-i18next";
import { ReactSVG } from "react-svg";
import { getIcon } from "@/constants";
import { Link, usePage } from "@inertiajs/react";
import { truncate, map } from "lodash";
import { User } from "@/types";
import { motion } from "framer-motion";

// Animation variants
const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: {
            staggerChildren: 0.08,
        },
    },
};

const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
        opacity: 1,
        y: 0,
        transition: {
            duration: 0.5,
        },
    },
};

interface TeacherCardProps {
    teacher: User;
}

const TeacherCard: React.FC<TeacherCardProps> = ({ teacher }) => {
    const { currentLang }: any = usePage().props;
    return (
        <motion.div variants={cardVariants}>
            <Link
                href={`${route(`${currentLang}.frontend.user.show`, teacher.id)}?slug=${teacher.name}`}
                className="flex flex-col justify-center items-center bg-gray-50 rounded-2xl p-6 hover:bg-white hover:shadow-md transition-all duration-300 group border border-gray-100"
            >
                <div className="relative mb-4">
                    <div className="w-20 h-20 md:w-24 md:h-24 rounded-full bg-gray-200 overflow-hidden">
                        <img
                            loading="lazy"
                            src={teacher.thumb}
                            alt={teacher.name}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                    </div>
                </div>

                <div className="text-center w-full">
                    <h3 className="text-sm md:text-base font-bold text-gray-800 leading-tight">
                        {truncate(`${teacher.title?.name || ""} ${teacher.name}`, {
                            length: 20,
                        })}
                    </h3>
                </div>
            </Link>
        </motion.div>
    );
};

interface TeachersSectionProps {
    teachers: User[];
}

const TeachersSection: React.FC<TeachersSectionProps> = ({ teachers }) => {
    const { t } = useTranslation();

    if (!teachers || teachers.length === 0) {
        return null;
    }

    return (
        <div className="bg-white py-12 sm:py-8 rounded-3xl">
            <div className="container mx-auto px-6">
                {/* Header Section */}
                <div className="flex flex-1 flex-col justify-center items-center gap-y-2 mb-12">
                    <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl capitalize">
                        {t("our_lecturers")}
                    </h2>

                    {/* Decorative underline */}
                    <ReactSVG
                        src={getIcon("title_arrow.svg")}
                        className="relative w-20 text-orange-700 ltr:rotate-180"
                    />

                    {/* <p className="text-md leading-8 text-balance text-gray-400 text-center max-w-4xl">
                        {t("lecturers_description")}
                    </p> */}
                </div>

                {/* Teachers Grid */}
                <motion.div
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, margin: "-50px" }}
                    variants={containerVariants}
                    className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6"
                >
                    {map(teachers, (teacher, index) => (
                        <TeacherCard key={index} teacher={teacher} />
                    ))}
                </motion.div>
            </div>
        </div>
    );
};

export default TeachersSection;
